import { useEffect, useRef, useState } from 'react';
import { IoNewspaperOutline, IoClose } from 'react-icons/io5';
import { Gpt } from 'react-gpt-ads';
import axios from 'axios';
import moment from 'moment';
import i18next from 'i18next';
import { getStateBySlug } from '../../config/states';
import VideoSection from '../Home/VideoSection';
import ErrorImage from '../../img/og-image.jpg';
import { trackEvent } from '../../utils/analytics';
import { fetchArticleGallery } from '../../utils/articleGallery';
import './RelatedNewsPanel.css';

const replaceErrorImg = (e) => {
  e.target.onerror = null;
  e.target.src = ErrorImage;
};

const NewsRow = ({ data }) => (
  <a
    className="rnp-row"
    href={data.url}
    target="_blank"
    rel="noreferrer"
    onClick={() => trackEvent('article_click', { url: data.url, title: data.title, location: 'related_news_panel' })}
  >
    <div className="rnp-row__media">
      <img src={data.picurl} alt={data.title} loading="lazy" onError={replaceErrorImg} />
    </div>
    <div className="rnp-row__body">
      <h4 className="rnp-row__title">{data.title}</h4>
      <time className="rnp-row__date">{moment(data.date).fromNow()}</time>
    </div>
  </a>
);

const RelatedNewsPanel = ({ state }) => {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [articles, setArticles] = useState([]);
  const [videos, setVideos] = useState([]);
  const cancelRef = useRef(false);

  const stateConfig = getStateBySlug(state);
  const tag = stateConfig ? '森美蘭州選（森州選舉）' : '';

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    setLoaded(false);
    setArticles([]);
    setVideos([]);
  }, [tag]);

  useEffect(() => {
    if (!open || loaded || !tag) return;
    cancelRef.current = false;
    setLoading(true);
    const lang = localStorage.getItem('lang') || 'zh_CN';

    Promise.allSettled([
      axios.get(`article_tag.php?tag=${encodeURIComponent(tag)}&lang=${lang}`, {
        timeout: 7000,
      }),
      fetchArticleGallery(),
    ]).then((results) => {
      if (cancelRef.current) return;
      const [newsRes, homeRes] = results;
      if (newsRes.status === 'fulfilled') {
        setArticles(newsRes.value.data?.articles || []);
      }
      if (homeRes.status === 'fulfilled') {
        setVideos(homeRes.value?.videos || []);
      }
      setLoaded(true);
      setLoading(false);
    });

    return () => {
      cancelRef.current = true;
    };
  }, [open, loaded, tag]);

  return (
    <>
      <button
        type="button"
        className="rnp-fab"
        onClick={() => {
          trackEvent('related_news_open');
          setOpen(true);
        }}
        aria-label={i18next.t('相关新闻')}
      >
        <IoNewspaperOutline />
      </button>

      <div
        className={`rnp-backdrop ${open ? 'is-open' : ''}`}
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />

      <aside
        className={`rnp-drawer ${open ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={i18next.t('相关新闻')}
      >
        <header className="rnp-drawer__head">
          <h3 className="rnp-drawer__title">
            {i18next.t('相关新闻')}
            {tag && <span className="rnp-drawer__tag">#{tag}</span>}
          </h3>
          <button
            type="button"
            className="rnp-drawer__close"
            onClick={() => setOpen(false)}
            aria-label="Close"
          >
            <IoClose />
          </button>
        </header>

        <div className="rnp-drawer__body">
          {loading && <div className="rnp-empty">{i18next.t('加载中...')}</div>}
          {!loading && loaded && articles.length === 0 && (
            <div className="rnp-empty">{i18next.t('暂无相关新闻')}</div>
          )}
          {!loading &&
            articles.map((a, idx) => (
              <div key={a.id || idx}>
                <NewsRow data={a} />
                {idx === Math.min(7, articles.length - 1) && videos.length > 0 && (
                  <div className="rnp-video-wrap">
                    <VideoSection videos={videos} />
                  </div>
                )}
                {idx === 3 && (
                  <div className="rnp-ad-slot" aria-label="Advertisement">
                    <Gpt
                      adUnit="/2163006/election_300x250_336x280_3"
                      name="div-gpt-ad-1599907131243-0"
                      size={[[300, 250], [336, 280], 'fluid']}
                    />
                  </div>
                )}
              </div>
            ))}
        </div>
      </aside>
    </>
  );
};

export default RelatedNewsPanel;
